import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useSchemaEntry } from "@/components/SchemaGraph";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  imageAlt?: string;
  url: string;
  type?: string;
  noindex?: boolean;
  /** Use the title exactly as given, without the brand suffix. */
  exactTitle?: boolean;
}

// Ensure image URL is absolute
const getAbsoluteImageUrl = (image: string): string => {
  // Already absolute URL
  if (image.startsWith('http://') || image.startsWith('https://')) {
    return image;
  }
  // Relative path - make it absolute
  const cleanPath = image.startsWith('/') ? image : `/${image}`;
  return `https://linkzy.io${cleanPath}`;
};

export const SEOHead = ({
  title,
  description,
  keywords,
  image = "/images/linkzy-og-default.png",
  imageAlt,
  url,
  type = "website",
  noindex = false,
  exactTitle = false,
}: SEOHeadProps) => {
  // Keep titles within the ~60-char SEO limit: only append the brand suffix when it fits.
  const withSuffix = `${title} | Linkzy`;
  const fullTitle = exactTitle || title.includes("| Linkzy")
    ? title
    : withSuffix.length <= 60
      ? withSuffix
      : title;
  useSchemaEntry(noindex ? {} : { title: fullTitle, description });
  const canonicalUrl = `https://linkzy.io${url}`;
  const absoluteImageUrl = getAbsoluteImageUrl(image);
  const resolvedImageAlt = imageAlt || `${title} — Linkzy quality backlinks`;

  // The static index.html ships fallback meta tags for non-JS crawlers.
  // Once a route renders, drop the ones react-helmet does not own so pages
  // never expose duplicate description/og/twitter tags.
  useEffect(() => {
    const selectors = [
      'meta[name="description"]',
      'meta[name="keywords"]',
      'meta[property^="og:"]',
      'meta[name^="twitter:"]',
    ];
    document.head
      .querySelectorAll<HTMLMetaElement>(selectors.join(","))
      .forEach((el) => {
        if (!el.hasAttribute("data-react-helmet")) el.remove();
      });
  }, [canonicalUrl]);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImageUrl} />
      <meta property="og:image:secure_url" content={absoluteImageUrl} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={resolvedImageAlt} />
      <meta property="og:site_name" content="Linkzy" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImageUrl} />
      <meta name="twitter:image:alt" content={resolvedImageAlt} />
      <meta name="twitter:site" content="@linkzyio" />

      {/* Additional SEO Tags */}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <meta name="googlebot" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content="Linkzy" />
    </Helmet>
  );
};
